import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ChevronUp,
  Heart,
} from "lucide-react";

const Footer = ({ onOpenLivecam }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const footerLinks = {
    "Hot Springs": [
      {text: "Find Hot Springs", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Featured Locations", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Map & Directions", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Temperature Guide", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Booking Information", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Livecam", onClick: onOpenLivecam},
    ],
    Resources: [
      {text: "Wellness Blog", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Travel Guides", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Health Benefits", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Safety Guidelines", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "FAQ", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
    ],
    Company: [
      {text: "About Us", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Our Story", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Contact", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Press", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Careers", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
    ],
    Support: [
      {text: "Help Center", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Accessibility", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Privacy Policy", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Terms of Service", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
      {text: "Cookie Policy", href: "https://sapiendra.ai/kato-experiment", target: "_blank", rel: "noopener noreferrer"},
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: "#", name: "Facebook" },
    { icon: Instagram, href: "#", name: "Instagram" },
    { icon: Twitter, href: "#", name: "Twitter" },
    { icon: Youtube, href: "#", name: "YouTube" },
  ];

  return (
    <footer className="bg-gray-900 text-white relative">
      {/* Back to top button */}
      <motion.button
        onClick={scrollToTop}
        className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-spa-blue-600 hover:bg-spa-blue-700 rounded-full flex items-center justify-center shadow-lg transition-colors duration-200"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronUp className="w-6 h-6" />
      </motion.button>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand section */}
          <div className="lg:col-span-1">
            <motion.div
              className="flex items-center space-x-2 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/logos/full_logo_horiztonal_white.png" 
                alt="Mankato Hot Springs Logo"
                className="h-8" 
              />
            </motion.div>

            <motion.p
              className="text-gray-400 text-sm leading-relaxed mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Discover the therapeutic warmth and natural beauty of Minnesota&#39;s
              finest hot springs and wellness destinations.
            </motion.p>

            {/* Contact info */}
            <motion.div
              className="space-y-3 text-sm text-gray-400"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-spa-blue-400" />
                <span>Mankato, Minnesota</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-spa-blue-400" />
                <span>(507) 555-SPRINGS</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-spa-blue-400" />
                <span>info@mankatosprings.com</span>
              </div>
            </motion.div>
          </div>

          {/* Footer links */}
          {Object.entries(footerLinks).map(
            ([category, links], categoryIndex) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (categoryIndex + 1) }}
                viewport={{ once: true }}
              >
                <h4 className="text-white font-semibold mb-4">{category}</h4>
                <ul className="space-y-2">
                  {links.map((link, index) => (
                    <li key={index}>
                      {typeof link === 'object' && link.onClick ? (
                        <button
                          onClick={link.onClick}
                          className="text-gray-400 hover:text-spa-blue-400 text-sm transition-colors duration-200 w-full text-left"
                        >
                          {link.text}
                        </button>
                      ) : (
                        <a
                          href={typeof link === 'object' ? link.href : '#'}
                          target={typeof link === 'object' ? link.target : undefined}
                          className="text-gray-400 hover:text-spa-blue-400 text-sm transition-colors duration-200"
                        >
                          {typeof link === 'object' ? link.text : link}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )
          )}
        </div>

        {/* Social media and bottom section */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Social media links */}
            <motion.div
              className="flex items-center space-x-4"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-gray-400 text-sm mr-2">Follow us:</span>
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-gray-800 hover:bg-spa-blue-600 rounded-full flex items-center justify-center transition-all duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </motion.div>

            {/* Copyright */}
            <motion.div
              className="text-center md:text-right"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-gray-400 text-sm flex items-center justify-center md:justify-end">
                 Made with
                <Heart className="w-4 h-4 text-red-500 mx-1 fill-current" />
                in Minnesota
              </p>
              
            </motion.div>
          </div>
        </div>

        {/* Additional info bar */}
        <motion.div
          className="mt-8 pt-6 border-t border-gray-800/50 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-500 text-xs">
            Always consult with a healthcare professional before using hot
            springs if you have medical conditions. Water temperatures and
            conditions may vary. Check with individual locations for current
            information.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
